import type { FormType, ReportJSON } from '@aloft/toolkit-shared';
import { AloftDate, isDefined } from '@aloft/toolkit-shared';

import { useCallback } from 'react';

import { useTkv2OrderID } from '../../controller/hooks/useTkv2OrderID.ts';
import { generateExportPdfBlobs } from '../../modules/commentary/export/generateExportPdfBlobs.tsx';
import { saveExportPdf } from '../../modules/commentary/export/saveExportPdf.tsx';
import { saveClickFormsFile } from '../../modules/export/clickforms/saveClickFormsFile.ts';
import { saveRptiFile } from '../../modules/export/sfrep/saveRptiFile.ts';
import { saveTdcxFile } from '../../modules/export/total/saveTdcxFile.ts';
import { useTkv2SiteValueChartsParams } from '../../modules/referenceView/utils/useTkv2SiteValueChartsParams.ts';
import { selectAllCommentaryData } from '../../redux/features/selectors/commentarySelectors/selectAllCommentaryData.ts';
import {
  selectCompSequence,
  selectEffectiveDate,
} from '../../redux/features/selectors/orderSelectors.ts';
import {
  selectOrderHasExternalDepreciation,
  selectOrderHasFunctionalDepreciation,
} from '../../redux/features/selectors/selectOrderDepreciation.ts';
import { selectReportJSON } from '../../redux/features/selectors/selectReportJSON.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import type { ClickFormsExisting, ExportType } from './types.ts';

export const useHandleExportClick = () => {
  const rawEffectiveDate = useAppSelector(selectEffectiveDate);
  const effectiveDate = isDefined(rawEffectiveDate)
    ? AloftDate.schema.parse(rawEffectiveDate)
    : undefined;
  const reportJSON = useAppSelector(selectReportJSON) as ReportJSON;
  const compSequence = useAppSelector(selectCompSequence);
  const siteValueChartParams = useTkv2SiteValueChartsParams();
  const orderID = useTkv2OrderID();

  const commentaryData = useAppSelector(selectAllCommentaryData);
  const hasExternalDepreciation = useAppSelector(selectOrderHasExternalDepreciation);
  const hasFunctionalDepreciation = useAppSelector(selectOrderHasFunctionalDepreciation);

  const propertyStreetAddress = commentaryData ? commentaryData.subjectAddressDisplay.street : '';

  return useCallback(
    async (
      exportType: ExportType | undefined,
      formType: FormType,
      clickFormsExisting: ClickFormsExisting | undefined,
      setExportClicked: (val: boolean) => void,
      includeAnnotations: boolean,
    ) => {
      setExportClicked(true);

      const pdfs = commentaryData
        ? await generateExportPdfBlobs(
            commentaryData,
            siteValueChartParams,
            hasExternalDepreciation,
            hasFunctionalDepreciation,
          )
        : [];

      switch (exportType) {
        case 'appraise-it': // rpti
          await saveRptiFile({
            effectiveDate,
            zipName: propertyStreetAddress,
            reportJSON,
            pdfs,
            formType,
          });
          break;
        case 'click-forms':
          await saveClickFormsFile({
            compSequence,
            reportJSON,
            zipName: propertyStreetAddress,
            formType,
            isMerge: clickFormsExisting === 'existing',
          });
          break;
        case 'pdf':
          if (commentaryData) {
            await saveExportPdf(pdfs, propertyStreetAddress);
          }

          break;
        case 'total':
          await saveTdcxFile({
            reportJSON,
            zipName: propertyStreetAddress,
            formType,
            orderID,
            pdfs,
            includeAnnotations,
          });
          break;
        default:
          break;
      }

      setExportClicked(false);
    },
    [
      commentaryData,
      compSequence,
      effectiveDate,
      hasExternalDepreciation,
      hasFunctionalDepreciation,
      orderID,
      propertyStreetAddress,
      reportJSON,
      siteValueChartParams,
    ],
  );
};
